import AttendeeTicketNotification from '@eventbrite/attendee-ticket-notification';
import { CoreApplicationContext } from '@eventbrite/context-gen';
import { Layout } from '@eventbrite/eds-layout';
import { isMediumNarrowDown } from '@eventbrite/eds-utils';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { checkGate, updateUser } from '@eventbrite/statsig';
import loadable from '@loadable/component';
import React from 'react';
import { connect } from 'react-redux';
import { ConsumerHeader } from '../../../../components/ConsumerHeader';
import { FullTakeoverLoadingScreen } from '../../../../components/FullTakeoverLoadingScreen';
import { GA_HOME_FEED_CATEGORY } from '../../../../constants/analytics';
import {
    GetExperimentStatus,
    useGetStatsigExperiment,
} from '../../../../hooks';
import {
    Buckets,
    Events,
    FeatureFlags,
    PlaceObject,
} from '../../../../types/index';
import { isLocalStorageSupported } from '../../../../utils/localStorage';
import { BasePage } from '../../../base';
import withStickBehavior from '../../hoc/withStickyBehavior';
import { updateHeaderImage } from '../../redux/header';
import { getRestOfContext } from '../../redux/root';
import { runSearch } from '../../redux/search';
import { getFeedTrackingProperties } from '../../utils';
import './HomeBaseContainer.scss';

const BrowseLayout = loadable(
    () => import('../../experiments/homepageEngagement/BrowseLayout'),
);
const EventLayout = loadable(
    () => import('../../experiments/homepageEngagement/EventLayout'),
);
const BrowseContainer = loadable(() => import('../BrowseContainer'), {
    resolveComponent: (components: any) => components.BrowseContainer,
});

const HeaderContainer = loadable(() => import('../HeaderContainer'), {
    resolveComponent: (components: { HeaderContainer: React.ReactElement }) =>
        components.HeaderContainer,
    fallback: <div style={{ height: '300px' }}></div>,
});
const FullbleedHeaderContainer = loadable(
    () => import('../FullbleedHeaderContainer'),
    {
        resolveComponent: (components: {
            FullbleedHeaderContainer: React.ReactElement;
        }) => components.FullbleedHeaderContainer,
        fallback: <div className="fullbleed-header-fallback"></div>,
    },
);

const USBannerContainer = loadable(() => import('../USBannerContainer'), {
    resolveComponent: (components: { USBannerContainer: React.ReactElement }) =>
        components.USBannerContainer,
});

const RecFeedbackContainer = loadable(() => import('../RecFeedbackContainer'));

const isFeatureFlagRecFeedbackStatusPass = checkGate('rec_feedback');

const EnhancedHomeConsumerHeader = withStickBehavior(ConsumerHeader);

//Connect BasePage with extra content
const _mapStateToProps = (
    {
        app,
        user,
        content,
    }: {
        app: { isMobile?: boolean; featureFlags: FeatureFlags };
        user: { isAuthenticated: boolean; publicId: string };
        content: { seasonal: any[] };
    },
    ownProps: any,
) => ({
    ...ownProps,
    isMobile: app.isMobile,
    isAuthenticated: user.isAuthenticated,
    userId: user.publicId,
    featureFlags: app.featureFlags,
    seasonalContent: content.seasonal,
});

const _mapDispatchToProps = {
    handleSearch: runSearch,
};

const ConnectedBasePage = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(BasePage);

// Deal with the messed up Varnish urls (/esi_cahe/?...) which break the signin link
const fixLoginUrl = (loginUrl: string) => {
    const splitUrl = loginUrl.split('/');
    const urlBeginning = splitUrl.slice(0, -1);

    return urlBeginning.concat('?referrer=%2F').join('/');
};

type ExperimentValues = 'loading' | 0 | 1 | 2;
const UK_TLD = '.co.uk';

//Leaving .com in here for testing on QA. Will be removed before launch
const EXPERIMENT_TLDS = [UK_TLD];

const FunctionalHomeBaseContainerWrapper: React.FunctionComponent<
    HomeBaseContainerProps
> = (props) => {
    //The prop as defined in the server render (via ESI) is should_enable_experimentation
    //while the prop defined on the context blob on __SERVER_DATA__ is enable_experimentation.
    //Due to this mismatch and uncertainty on core releases, grabbing both.
    const { enable_experimentation, should_enable_experimentation, env } =
        props;

    //In case should_enable_experimentation is undefined
    //casting the value to a boolean
    const experimentationEnabled = !!(
        enable_experimentation || should_enable_experimentation
    );

    const enabled =
        experimentationEnabled &&
        EXPERIMENT_TLDS.includes(`${env?.localeInfo?.tld}`);

    const homepageEngagementExperiment = useGetStatsigExperiment(
        'homepage_engagement',
        'homepage_variant',
        0,
        enabled,
    );

    const parsedVariant =
        homepageEngagementExperiment.status === GetExperimentStatus.SUCCESS
            ? parseInt(homepageEngagementExperiment.variant)
            : GetExperimentStatus.LOADING;

    return (
        <HomeBaseContainer
            {...props}
            experimentVariant={parsedVariant as ExperimentValues}
            experimentEnabled={enabled}
        />
    );
};

interface HomeBaseContainerProps {
    buckets: Buckets;
    featureFlags?: FeatureFlags;
    location: Location;
    placeId?: string;
    latitude?: number;
    longitude?: number;
    currentPlace?: string;
    currentPlaceParent?: string;
    locations?: {
        places: PlaceObject[];
    };
    placeType?: string;
    slug?: string;
    flatBucket?: {
        results: Events;
    };
    isAuthenticated?: boolean;
    userId?: string;
    handleRestOfContext?: Function;
    originalContext?: object;
    history?: object;
    env?: CoreApplicationContext['env'];
    updateHeaderImage?: Function;
    enable_experimentation?: boolean;
    should_enable_experimentation?: boolean;
    experimentVariant?: ExperimentValues;
    experimentEnabled?: boolean;
    header?: {
        forceSpecialStyles: boolean;
    };
    app: {
        loadingState: { isFeLoading: boolean };
    };
    tabKey?: string;
}

interface HomeBaseContainerState {
    isInBrowser?: boolean;
    isMobile?: boolean;
    isLocalStorageRecFeedbackViewed?: boolean;
}

export class HomeBaseContainer extends React.Component<
    HomeBaseContainerProps,
    HomeBaseContainerState
> {
    constructor(props: Readonly<HomeBaseContainerProps>) {
        super(props);

        this.state = {
            isInBrowser: false,
            isMobile: false,
        };

        props.updateHeaderImage?.();
    }

    componentDidMount() {
        const {
            handleRestOfContext,
            originalContext,
            isAuthenticated,
            userId,
        } = this.props;

        handleRestOfContext?.(originalContext);

        this.setState({
            isInBrowser: true,
            isMobile: isMediumNarrowDown(),
        });

        const feedTrackingProperties = getFeedTrackingProperties(
            isAuthenticated,
            userId,
        );

        trackAnalyticsEvent(feedTrackingProperties);

        if (HAS_WINDOW) {
            this.setState({
                // set true if localStorage is supported and user already saw feedback form
                // also, set true if localStorage is NOT supported. We don't want to continually
                // show the feedback form.
                isLocalStorageRecFeedbackViewed: isLocalStorageSupported()
                    ? window?.localStorage?.getItem(
                          'search:recFeedback:viewed',
                      ) === 'true'
                    : true,
            });
        }
    }

    componentDidUpdate(prevProps: HomeBaseContainerProps) {
        const { app, userId } = this.props;
        const prevUserId = prevProps?.userId;

        //Only update user when the userId changes
        if (
            this.state.isInBrowser &&
            !app.loadingState.isFeLoading &&
            userId !== prevUserId
        ) {
            updateUser({ user: { userID: userId } });
        }
    }

    getControlLayout() {
        const { header, env } = this.props;
        const useNewHeader = header?.forceSpecialStyles;

        return (
            <>
                {useNewHeader && (
                    <FullbleedHeaderContainer history={this.props.history} />
                )}
                <Layout hasHorizontalGutters={true} maxWidth="large">
                    {env?.localeInfo.tld === '.com' ? (
                        <div className="eds-l-pad-top-3">
                            <USBannerContainer />
                        </div>
                    ) : null}
                    <>
                        {!useNewHeader && (
                            <HeaderContainer history={this.props.history} />
                        )}
                    </>
                    <BrowseContainer />
                </Layout>
            </>
        );
    }

    //Cleanup after experiment over
    //https://eventbrite.atlassian.net/browse/EB-200301
    getHomePageLayout() {
        const {
            experimentEnabled,
            experimentVariant,
            location,
            isAuthenticated,
        } = this.props;

        if (experimentEnabled && experimentVariant !== undefined) {
            const MAP: Record<any, any> = {
                loading: () => <></>,
                1: EventLayout,
                2: BrowseLayout,
            };

            const HomeLayout = MAP[experimentVariant];

            return (
                <FullTakeoverLoadingScreen
                    isInBrowser={experimentVariant !== 'loading'}
                >
                    {experimentVariant === 0 ? (
                        this.getControlLayout()
                    ) : (
                        <HomeLayout
                            location={location}
                            isAuthenticated={isAuthenticated}
                        />
                    )}
                </FullTakeoverLoadingScreen>
            );
        }

        return this.getControlLayout();
    }

    canShowRecFeedbackForm() {
        return (
            isFeatureFlagRecFeedbackStatusPass &&
            this.props.tabKey === 'for_you' &&
            !this.state.isLocalStorageRecFeedbackViewed
        );
    }

    render() {
        // During the server render, because of Varnish, we want to render the page
        // as a logged-out user. Once we are in the browser, we have the props for the user
        // and we want to update the page. Since most of our components do not keep this in mind,
        // we basically have to do it with componentDidMount or else we get weird markup mismatch issues.
        // Once we're in the browser, we'll go to use the normal props.
        // See also the ConsumerHeader/index.js
        const overrideProps = this.state.isInBrowser
            ? {}
            : {
                  env: {
                      ...this.props.env,
                      loginUrl: fixLoginUrl(this.props.env?.loginUrl || ''),
                      signinUrl: fixLoginUrl(this.props.env?.signinUrl || ''),
                      signupUrl: fixLoginUrl(this.props.env?.signupUrl || ''),
                      showCookieHeader: false,
                  },
                  footer: <div />,
                  user: {
                      isAuthenticated: false,
                      publicId: null,
                      canCreateEvents: false,
                  },
              };

        const layout = this.getHomePageLayout();

        return (
            <ConnectedBasePage
                {...this.props}
                {...overrideProps}
                isHomePage={true}
                structureHeader={EnhancedHomeConsumerHeader}
            >
                <AttendeeTicketNotification
                    shouldAnimateEntry={true}
                    isAuthenticated={this.props.isAuthenticated}
                    containerStyles={
                        this.state.isMobile
                            ? { margin: '0 auto', top: '120px' }
                            : { right: '32px', top: '120px' }
                    }
                    onLinkClick={(eid: string, hasDigitalContent: boolean) =>
                        trackAnalyticsEvent({
                            category: GA_HOME_FEED_CATEGORY,
                            label: eid,
                            action: hasDigitalContent
                                ? 'UpcomingOnlineEventViewed'
                                : 'UpcomingTicketViewed',
                        })
                    }
                    onDismiss={(
                        timeUntilEvent: string,
                        hasDigitalContent: boolean,
                    ) =>
                        trackAnalyticsEvent({
                            category: GA_HOME_FEED_CATEGORY,
                            label: timeUntilEvent,
                            action: hasDigitalContent
                                ? 'UpcomingOnlineEventDismissed'
                                : 'UpcomingTicketDismissed',
                        })
                    }
                    domNode=""
                    animateEntryDirection="downwards"
                />
                {layout}
                {this.canShowRecFeedbackForm() ? (
                    <RecFeedbackContainer />
                ) : null}
            </ConnectedBasePage>
        );
    }
}

const _mapHomeStateToProps = ({
    app,
    user,
    env,
    header,
    content,
}: {
    app: {
        isMobile?: boolean;
        loadingState: { isFeLoading: boolean };
    };
    user: {
        isAuthenticated?: boolean;
        publicId?: string;
    };
    env: CoreApplicationContext['env'];
    header: object;
    content: {
        browseState?: {
            tabKey?: string;
        };
    };
}) => ({
    isMobile: app.isMobile,
    isAuthenticated: user.isAuthenticated,
    userId: user.publicId,
    env,
    app,
    header,
    tabKey: content.browseState?.tabKey,
});

const _mapHomeDispatchToProps = {
    handleSearch: runSearch,
    handleRestOfContext: getRestOfContext,
    updateHeaderImage: updateHeaderImage,
};

export default connect(
    _mapHomeStateToProps,
    _mapHomeDispatchToProps,
)(FunctionalHomeBaseContainerWrapper);
